import styles from './SearchBar.less';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { debounce, get } from 'lodash';
import IsolatedScroll from 'react-isolated-scroll';
import { isClientMobileWidth } from 'src/modules/responsive-helpers';

import smoothScroll from 'src/modules/smooth-scroll';

import {
  Button,
  ChevronIcon,
  ClearField,
  Hidden,
  PageBlock,
  ScreenReaderOnly,
  ScreenReaderSkipTarget,
  SearchIcon,
  Text,
  TextField
} from 'seek-style-guide/react';

import {
  updateKeywordsField,
  whereSuggestionSelected,
  searchSubmit,
  selectClassification,
  selectWhere,
  updateActiveRefinement,
  clearActiveRefinement,
  runSearch
} from 'src/store/search/search';

import NavLink from 'src/components/NavLink/NavLink';
import ClassificationList from './ClassificationList/ClassificationList';
import WhereField from './WhereField/WhereField';
import LastSearch from './LastSearch/LastSearch';
import MoreOptions from './MoreOptions/MoreOptions';
import HelpLink from './HelpLink/HelpLink';

const shouldShowCloseButton = (isOpen, isMobileWidth) =>
  !(isOpen && isMobileWidth);

const { func, string, shape, array, bool, object } = PropTypes;

function mapStateToProps({ search, lastSearch }) {
  return {
    keywordsField: search.keywordsField,
    whereField: search.whereField,
    query: search.query,
    hasLastSearch: Boolean(lastSearch.query),
    classifications: get(search, 'refinements.classification', []),
    clearClassification: get(search, 'refinements.clear.classification'),
    classificationListOpen: search.activeRefinement === 'Classification'
  };
}

const mapDispatchToProps = {
  onSubmit: searchSubmit,
  updateKeywordsField,
  whereSuggestionSelected,
  selectClassification,
  selectWhere,
  updateActiveRefinement,
  closeClassificationList: clearActiveRefinement,
  runSearch
};

export const correctBackdropPosition = ref => {
  if (ref) {
    ref.style.left = '0px';
    ref.style.top = '0px';

    const { left, top } = ref.getBoundingClientRect();
    ref.style.left = `${left >= 0 ? '-' : ''}${left}px`;
    ref.style.top = `${top >= 0 ? '-' : ''}${top}px`;
  }
};

class SearchBar extends Component {
  static propTypes = {
    keywordsField: string.isRequired,
    whereField: string.isRequired,
    query: shape({
      keywords: string,
      where: string,
      classification: string,
      subclassification: string
    }).isRequired,
    hasLastSearch: bool.isRequired,
    classifications: array.isRequired,
    collapsed: bool.isRequired,
    selectClassification: func.isRequired,
    clearClassification: object,
    onSubmit: func.isRequired,
    updateKeywordsField: func.isRequired,
    whereSuggestionSelected: func.isRequired,
    selectWhere: func.isRequired,
    classificationListOpen: bool.isRequired,
    closeClassificationList: func.isRequired,
    updateActiveRefinement: func.isRequired,
    runSearch: func.isRequired
  };

  constructor() {
    super();

    this.debouncedRunSearch = debounce(this.runSearch, 50);
  }

  componentDidMount() {
    window.addEventListener('resize', this.resizeHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  resizeHandler = () => correctBackdropPosition(this.backdropEl);

  storeClassificationsToggleReference = classificationsToggle => {
    if (classificationsToggle) {
      this.classificationsToggle = classificationsToggle;
    }
  };

  onKeywordsChange = event => {
    this.props.updateKeywordsField(event.target.value);
  };

  onKeywordsClear = () => {
    this.props.updateKeywordsField('');
    this.debouncedRunSearch();
  };

  onKeywordsBlur = event => {
    const { keywords = '' } = this.props.query;
    const { value } = event.target;

    if (value !== keywords) {
      this.debouncedRunSearch();
    }
  };

  onSubmit = event => {
    event.preventDefault();

    this.props.onSubmit();
    this.debouncedRunSearch.cancel();
    this.runSearch('/jobs');
  };

  runSearch = pathname => {
    const { keywordsField, whereField, query } = this.props;

    this.props.runSearch({
      keywordsField,
      whereField,
      query,
      pathname
    });
  };

  onWhereSuggestionSelected = ({ suggestion, suggestionValue }) => {
    this.props.whereSuggestionSelected({ suggestion, suggestionValue });
    this.debouncedRunSearch();
  };

  onWhereClear = () => {
    this.debouncedRunSearch();
  };

  onWhereBlur = event => {
    const { where = '' } = this.props.query;

    if (event.target.value !== where) {
      this.debouncedRunSearch();
    }
  };

  onClassificationListKeyUp = event => {
    const ESCAPE_KEYCODE = 27;
    if (event.keyCode === ESCAPE_KEYCODE) {
      this.classificationsToggle.click();
      this.classificationsToggle.focus();
    }
  };

  onClearClassificationClick = event => {
    event.stopPropagation();
  };

  openClassificationList = () => {
    this.props.updateActiveRefinement('Classification');

    if (isClientMobileWidth()) {
      smoothScroll(this.classificationsToggle, { offset: 10 });
    }
  };

  toggleClassificationList = () => {
    const { classificationListOpen, closeClassificationList } = this.props;

    if (classificationListOpen) {
      closeClassificationList();
    } else {
      this.openClassificationList();
    }
  };

  setRef = ref => {
    this.backdropEl = ref;
    correctBackdropPosition(ref);
  };

  render() {
    const {
      keywordsField,
      query,
      hasLastSearch,
      classifications,
      collapsed,
      clearClassification,
      selectClassification: onClassficationSelect,
      classificationListOpen,
      closeClassificationList
    } = this.props;

    const { keywords } = query;
    const selectedClassifications = classifications.filter(
      item => item.isActive
    );
    const refinePath = collapsed ? '/jobs' : '/';
    const rootClassName = classnames({
      [styles.root]: true,
      [styles.root_isCollapsed]: collapsed,
      [styles.root_hasLastSearch]: hasLastSearch
    });
    const classificationGroupClasses = classnames({
      [styles.listItemField]: true,
      [styles.classificationsGroup]: true,
      [styles.classificationsGroup_hasSelections]:
        clearClassification &&
        shouldShowCloseButton(classificationListOpen, isClientMobileWidth())
    });

    return (
      <section className={rootClassName}>
        <PageBlock>
          <div className={styles.formContainer}>
            <ScreenReaderSkipTarget name="start-of-content" />

            <form
              aria-labelledby="PerformASearch"
              role="search"
              name="SearchBar"
              id="SearchBar"
              className={styles.form}
              method="get"
              action="/jobs"
              onSubmit={this.onSubmit}
            >
              <ScreenReaderOnly>
                <h1 id="PerformASearch">Perform a job search</h1>
              </ScreenReaderOnly>

              <Hidden mobile component={HelpLink} className={styles.helpItem} />

              <input
                type="hidden"
                name="classification"
                value={query.classification || ''}
              />
              <input
                type="hidden"
                name="subclassification"
                value={query.subclassification || ''}
              />

              <div className={styles.list}>
                <div className={`${styles.listItem} ${styles.listItem_isWhat}`}>
                  <div className={styles.listItemGroup}>
                    <div
                      role="region"
                      aria-label="Enter Keywords"
                      className={styles.listItemField}
                    >
                      <TextField
                        role="textbox"
                        id="SearchBar__Keywords"
                        className={styles.keywordsField}
                        message={false}
                        label="What"
                        labelProps={{ className: styles.listItemLabel }}
                        type="search"
                        value={keywordsField}
                        onChange={this.onKeywordsChange}
                        onBlur={this.onKeywordsBlur}
                        inputProps={{
                          name: 'keywords',
                          className: styles.keywordsInput,
                          maxLength: '500',
                          placeholder: 'Enter keywords',
                          autoComplete: 'off',
                          'data-automation': 'searchKeywordsField',
                          'aria-label': 'Keywords'
                        }}
                        onClear={this.onKeywordsClear}
                      />
                    </div>

                    <div
                      role="region"
                      aria-label="Job Classifications"
                      className={classificationGroupClasses}
                    >
                      <input
                        id="SearchBar__Classifications"
                        className={styles.classificationsToggle}
                        type="checkbox"
                        role="button"
                        checked={classificationListOpen}
                        aria-expanded={classificationListOpen}
                        aria-label="show classification list"
                        onClick={this.toggleClassificationList}
                        ref={this.storeClassificationsToggleReference}
                      />

                      <div
                        ref={this.setRef}
                        className={styles.classificationsBackdrop}
                      >
                        <label
                          data-automation="classificationDropDownListBackdrop"
                          className={styles.classificationsBackdropLabel}
                          onClick={closeClassificationList}
                        >
                          <ScreenReaderOnly>
                            Hide classifications
                          </ScreenReaderOnly>
                        </label>
                      </div>

                      <label
                        data-automation="classificationDropDownList"
                        htmlFor="SearchBar__Classifications"
                        className={`${styles.field} ${
                          styles.field_isClassifications
                        }`}
                      >
                        <ScreenReaderOnly>
                          Show classification list.{' '}
                        </ScreenReaderOnly>
                        <span className={styles.classificationsText}>
                          {{
                            0: 'Any Classification',
                            1: selectedClassifications[0]
                              ? selectedClassifications[0].label
                              : ''
                          }[selectedClassifications.length] ||
                            `${selectedClassifications.length} Classifications`}
                        </span>

                        <div className={styles.chevronContainer}>
                          <Hidden desktop className={styles.doneButton}>
                            Done
                          </Hidden>
                          <ChevronIcon
                            direction="down"
                            className={styles.chevron}
                            svgClassName={styles.chevronSvg}
                          />
                        </div>

                        <NavLink
                          title="Clear classifications"
                          className={styles.clearClassificationLink}
                          location={{
                            pathname: refinePath,
                            query: clearClassification || query
                          }}
                          aria-hidden={!clearClassification}
                          aria-label="Clear classifications"
                          tabIndex={clearClassification ? 0 : -1}
                          data-automation="clearAllClassifications"
                          rel="nofollow"
                          onClick={this.onClearClassificationClick}
                        >
                          <ClearField />
                        </NavLink>
                      </label>

                      <IsolatedScroll
                        id="classificationsPanel"
                        className={styles.classificationsPanel}
                      >
                        <ClassificationList
                          onKeyUp={this.onClassificationListKeyUp}
                          handleSelection={onClassficationSelect}
                          path={refinePath}
                          classifications={classifications}
                          open={classificationListOpen}
                        />
                      </IsolatedScroll>
                    </div>
                  </div>
                </div>

                <div
                  role="region"
                  aria-label="Enter a location"
                  className={styles.listItem}
                >
                  <WhereField
                    id="SearchBar__Where"
                    name="where"
                    label="Where"
                    labelProps={{ className: styles.listItemLabel }}
                    onSuggestionSelected={this.onWhereSuggestionSelected}
                    onInputBlur={this.onWhereBlur}
                    onClear={this.onWhereClear}
                  />
                </div>
              </div>

              <Button
                color="pink"
                type="submit"
                data-automation="searchButton"
                className={styles.button}
                aria-label="Submit search"
              >
                SEEK
              </Button>
            </form>

            <div className={styles.searchOptions}>
              <div className={styles.lastSearch} aria-hidden={collapsed}>
                {hasLastSearch && <LastSearch />}
              </div>

              <Hidden
                mobile
                className={styles.moreOptions}
                aria-hidden={collapsed}
              >
                <MoreOptions />
              </Hidden>
            </div>
          </div>
        </PageBlock>

        <div className={styles.summary}>
          <NavLink
            location={{ pathname: '/', query }}
            className={styles.summaryLink}
          >
            <Text subheading regular className={styles.summaryLinkText}>
              <SearchIcon
                className={styles.magnifyingGlass}
                svgClassName={styles.magnifyingGlassSvg}
              />
              {`${keywords || 'Jobs'} in ${get(
                selectedClassifications[0],
                'label',
                'All Classifications'
              )}`}
            </Text>
          </NavLink>
        </div>

        <div className={styles.pageBackdrop} />
      </section>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchBar);
